import { InvalidGltfManifestError, throwIfMeshLoadCancelled } from '@/plugins/mesh/errors'
import { DefaultGltfBatchPlanner } from '@/plugins/mesh/loaders/gltfLowMemory/DefaultGltfBatchPlanner'
import {
  DefaultGltfSceneAnalyzer,
  getAccessorTightByteLength,
} from '@/plugins/mesh/loaders/gltfLowMemory/DefaultGltfSceneAnalyzer'
import {
  combineGeometryEstimates,
  createEmptyGeometryEstimate,
  estimateGltfGeometryResources,
  getEstimatedGeometryBytes,
} from '@/plugins/mesh/loaders/gltfLowMemory/estimateGltfGeometryResources'
import { resolveGltfLoadingOptions } from '@/plugins/mesh/loaders/gltfLowMemory/lowMemoryOptions'
import { readGltfPreflightManifest } from '@/plugins/mesh/loaders/gltfLowMemory/preflightManifest'
import { GltfPreflightReader } from '@/plugins/mesh/loaders/gltfLowMemory/preflightRead'
import type {
  GltfGeometryResourceEstimate,
  GltfResourceBudget,
  GltfResourceCandidate,
  GltfResourcePreflightReport,
} from '@/plugins/mesh/loaders/gltfLowMemory/preflightTypes'
import type {
  GltfManifestDocument,
  GltfTextureInfo,
} from '@/plugins/mesh/loaders/gltfLowMemory/types'
import {
  describeMeshSource,
  getMeshSourceExtension,
  resolveMeshBaseUrl,
} from '@/plugins/mesh/loaders/loaderUtils'
import {
  normalizeMaxConcurrentLoads,
  parseMeshResultManifest,
} from '@/plugins/mesh/loaders/meshResultManifest'
import { resolveMeshOptimizationOptions } from '@/plugins/mesh/optimization/optimizationOptions'
import type {
  GltfLoadingOptions,
  GltfLoadingOptionsInput,
  MeshLoadRequest,
} from '@/plugins/mesh/types'

interface PreflightImage {
  readonly ktx2?: boolean
  /** 仅采用 JSON 声明的编码长度；外部图片未知时不额外请求。 */
  readonly encodedBytes?: number
}

interface PreflightTexture {
  readonly ktx2?: boolean
  readonly imageIndex: number
  readonly mipmaps: boolean
}

/** 可在单资产和分块清单之间复用的内部资源清单。 */
export interface GltfPreflightAsset {
  readonly document: GltfManifestDocument
  readonly images: readonly PreflightImage[]
  readonly textures: readonly PreflightTexture[]
  readonly geometry: GltfGeometryResourceEstimate
  readonly transientBytes: number
  readonly diagnostics: readonly string[]
  readonly readBytes: number
  readonly dracoUnknown?: boolean
}

/** 旧预检选项的增量扩展；只有显式设为 true 才采用 Draco 输出估算。 */
export type GltfResourcePreflightOptions = GltfLoadingOptionsInput & { readonly draco?: boolean }

function includeDracoGeometry(
  document: GltfManifestDocument,
  geometry: GltfGeometryResourceEstimate,
  primitives: readonly { meshIndex: number; primitiveIndex: number }[]
): { geometry: GltfGeometryResourceEstimate; compressedBytes: number; hasDraco: boolean } {
  const manifest = document.manifest
  const adjusted = { ...geometry }
  const seenPrimitives = new Set<string>()
  const seenAttributes = new Set<number>()
  const seenIndices = new Set<number>()
  const seenPositions = new Set<number>()
  let compressedBytes = 0
  let hasDraco = false
  for (const source of primitives) {
    const key = `${source.meshIndex}:${source.primitiveIndex}`
    if (seenPrimitives.has(key)) continue
    seenPrimitives.add(key)
    const primitive = manifest.meshes?.[source.meshIndex]?.primitives[source.primitiveIndex]
    if (!primitive) throw new InvalidGltfManifestError(document.source, 'primitive 索引无效')
    const extension = primitive.extensions?.KHR_draco_mesh_compression as
      | { bufferView?: number; attributes?: Record<string, number> }
      | undefined
    if (!extension) continue
    hasDraco = true
    const view = manifest.bufferViews?.[extension.bufferView ?? -1]
    if (!view || !Number.isSafeInteger(view.byteLength) || view.byteLength < 1)
      throw new InvalidGltfManifestError(
        document.source,
        'KHR_draco_mesh_compression bufferView 无效'
      )
    compressedBytes += view.byteLength
    if (!extension.attributes || !Object.keys(extension.attributes).length)
      throw new InvalidGltfManifestError(document.source, 'KHR_draco_mesh_compression 属性映射无效')
    for (const [semantic, id] of Object.entries(extension.attributes)) {
      const index = primitive.attributes[semantic]
      const accessor = index === undefined ? undefined : manifest.accessors?.[index]
      if (!Number.isSafeInteger(id) || id < 0 || !accessor || index === undefined)
        throw new InvalidGltfManifestError(
          document.source,
          'KHR_draco_mesh_compression 属性 ID/accessor 无效'
        )
      const tight = getAccessorTightByteLength(accessor)
      const stride = Math.ceil(tight / accessor.count / 4) * 4
      const output = accessor.count * stride
      if (!Number.isSafeInteger(output))
        throw new InvalidGltfManifestError(
          document.source,
          'KHR_draco_mesh_compression 输出成本溢出'
        )
      adjusted.vertexAttributeBytes += output - (seenAttributes.has(index) ? 0 : tight)
      if (semantic === 'POSITION') {
        if (seenPositions.has(index)) adjusted.vertexCount += accessor.count
        seenPositions.add(index)
      }
      seenAttributes.add(index)
    }
    if (primitive.indices !== undefined) {
      const accessor = manifest.accessors?.[primitive.indices]
      if (!accessor)
        throw new InvalidGltfManifestError(
          document.source,
          'KHR_draco_mesh_compression 索引 accessor 无效'
        )
      const tight = getAccessorTightByteLength(accessor)
      const output = accessor.count * 4
      if (!Number.isSafeInteger(output))
        throw new InvalidGltfManifestError(
          document.source,
          'KHR_draco_mesh_compression 索引成本溢出'
        )
      adjusted.indexBytes += output - (seenIndices.has(primitive.indices) ? 0 : tight)
      if (seenIndices.has(primitive.indices)) adjusted.indexCount += accessor.count
      seenIndices.add(primitive.indices)
    }
  }
  return { geometry: adjusted, compressedBytes, hasDraco }
}

/** 读取一个 glTF/GLB 的资源元数据，不解码图片或分配 Geometry。 */
export async function collectGltfPreflightAsset(
  request: MeshLoadRequest,
  format: 'gltf' | 'glb',
  loading: GltfLoadingOptions,
  existingDocument?: GltfManifestDocument,
  ktx2Configured?: boolean,
  dracoEnabled = false
): Promise<GltfPreflightAsset> {
  const preflightRequest = { ...request, gltf: loading }
  const reader = new GltfPreflightReader(preflightRequest)
  const document =
    existingDocument ?? (await readGltfPreflightManifest(preflightRequest, format, reader))
  throwIfMeshLoadCancelled(request.signal, document.source)
  const { manifest } = document
  const analysis = new DefaultGltfSceneAnalyzer().analyze(document, request.signal)
  const diagnostics = analysis.compatibilityIssues.map(
    (issue) => `估算覆盖不完整: ${issue.feature}`
  )
  const materialIndices = new Set(analysis.primitives.map((primitive) => primitive.materialIndex))
  const sourceIndices: number[] = []
  const sourceMap = new Map<number, number>()
  const textures: PreflightTexture[] = []
  const textureKeys = new Set<string>()
  const addTexture = (info: GltfTextureInfo | undefined, colorSpace: string) => {
    if (!info) return
    const texture = manifest.textures?.[info.index]
    const basis = texture?.extensions?.KHR_texture_basisu as { source?: number } | undefined
    const fallback = texture?.source === undefined ? undefined : manifest.images?.[texture.source]
    const usesBasis =
      basis !== undefined &&
      !(
        ktx2Configured === false &&
        !manifest.extensionsRequired?.includes('KHR_texture_basisu') &&
        fallback &&
        fallback.mimeType !== 'image/ktx2' &&
        (fallback.uri !== undefined || fallback.bufferView !== undefined)
      )
    const sourceIndex = (usesBasis ? basis?.source : texture?.source) ?? -1 - info.index
    const key = `${sourceIndex}:${texture?.sampler ?? 'default'}:${colorSpace}:${info.texCoord ?? 0}`
    if (textureKeys.has(key)) return
    textureKeys.add(key)
    let imageIndex = sourceMap.get(sourceIndex)
    if (imageIndex === undefined) {
      imageIndex = sourceIndices.length
      sourceIndices.push(sourceIndex)
      sourceMap.set(sourceIndex, imageIndex)
    }
    const minFilter =
      texture?.sampler === undefined ? undefined : manifest.samplers?.[texture.sampler]?.minFilter
    textures.push({
      imageIndex,
      mipmaps: minFilter !== 9728 && minFilter !== 9729,
      ktx2: usesBasis || manifest.images?.[sourceIndex]?.mimeType === 'image/ktx2',
    })
  }
  for (const index of materialIndices) {
    const material = index === undefined ? undefined : manifest.materials?.[index]
    addTexture(material?.pbrMetallicRoughness?.baseColorTexture, 'srgb')
    addTexture(material?.emissiveTexture, 'srgb')
    addTexture(material?.pbrMetallicRoughness?.metallicRoughnessTexture, 'linear')
    addTexture(material?.normalTexture, 'linear')
    addTexture(material?.occlusionTexture, 'linear')
    if (
      material?.extensions &&
      Object.keys(material.extensions).some((name) => name !== 'KHR_materials_unlit')
    ) {
      // 未覆盖材质扩展可能引用额外纹理，按全部声明纹理作保守补充。
      for (
        let textureIndex = 0;
        textureIndex < (manifest.textures?.length ?? 0);
        textureIndex += 1
      ) {
        addTexture({ index: textureIndex }, 'extension')
      }
    }
  }
  const images: PreflightImage[] = sourceIndices.map((index) => {
    const image = manifest.images?.[index]
    const view =
      image?.bufferView === undefined ? undefined : manifest.bufferViews?.[image.bufferView]
    return {
      encodedBytes: view?.byteLength,
      ktx2: textures.some((texture) => texture.imageIndex === sourceMap.get(index) && texture.ktx2),
    }
  })
  if (images.length > 0) {
    diagnostics.push(
      '按图片数量和完整档位方形尺寸粗略估算；未读取原图尺寸，不包含全尺寸解码回退峰值'
    )
    if (images.some((image) => image.encodedBytes === undefined))
      diagnostics.push('部分图片编码长度未知，未计入其编码缓冲')
  }
  if (images.some((image) => image.ktx2))
    diagnostics.push(
      'ktx2-gpu-estimated: KTX2 按纹理数量、完整档位方形尺寸、RGBA8 和完整 mip 链保守估算显存；原始尺寸未知，加载期按实际输出复核',
      'ktx2-cpu-demand-unknown: JSON 无法确定 KTX2 转码常驻数据及 Worker/WASM 峰值，CPU 总量仍未知',
      ...(ktx2Configured === undefined
        ? ['ktx2-source-selection-unknown: 未知转码配置，不将普通后备与 KTX2 双计']
        : [])
    )
  let geometry = estimateGltfGeometryResources(manifest, analysis)
  const draco = dracoEnabled
    ? includeDracoGeometry(document, geometry, analysis.primitives)
    : { geometry, compressedBytes: 0, hasDraco: false }
  geometry = draco.geometry
  if (draco.hasDraco)
    diagnostics.push('draco-worker-wasm-unknown: JSON 仅给出解码输出下界，Worker/WASM 内部峰值未知')
  let geometryBytes = getEstimatedGeometryBytes(geometry)
  const sourceBytes = manifest.buffers?.reduce((sum, buffer) => sum + buffer.byteLength, 0) ?? 0
  // standard 保留原始 buffer，并可能在优化时复制属性；作为 fallback 的保守峰值。
  let transientBytes = sourceBytes * 2 + geometryBytes + draco.compressedBytes
  const optimization = resolveMeshOptimizationOptions(undefined, request.optimization)
  if (
    (loading.mode !== 'standard' || optimization.enabled) &&
    analysis.compatibilityIssues.length === 0
  ) {
    const plan = new DefaultGltfBatchPlanner().plan(document, analysis, {
      loading: {
        ...loading,
        budget: { ...loading.budget, maxFinalGeometryBytes: Infinity, maxTransientBytes: Infinity },
      },
      optimization,
      signal: request.signal,
    })
    geometry = combineGeometryEstimates(
      geometry,
      estimateGltfGeometryResources(manifest, analysis, plan)
    )
    geometryBytes = getEstimatedGeometryBytes(geometry)
    transientBytes = Math.max(transientBytes, plan.memory.estimatedTransientBytes)
  }
  return {
    document,
    images,
    textures,
    geometry,
    transientBytes,
    diagnostics,
    readBytes: reader.readBytes,
    dracoUnknown: draco.hasDraco,
  }
}

function available(value: number | undefined, ratio: number): number | undefined {
  if (value === undefined) return undefined
  if (!Number.isFinite(value) || value < 0) throw new RangeError('资源快照必须是非负有限字节数')
  return Math.floor(value * ratio)
}

/** 是否提供了需要在正式加载前检查的资源快照。 */
export function hasGltfResourceSnapshot(loading: GltfLoadingOptions): boolean {
  const snapshot = loading.texture.autoSize
  return (
    snapshot.availableSystemMemoryBytes !== undefined ||
    snapshot.availableGpuMemoryBytes !== undefined ||
    snapshot.totalGpuMemoryBytes !== undefined
  )
}

function configuredBudget(value: number | undefined): number {
  if (value === undefined || value === Infinity) return Infinity
  if (!Number.isFinite(value) || value < 0) throw new RangeError('预算必须为非负字节数或 Infinity')
  return value
}

/** 报告用 null 表达未配置，不用 Infinity 冒充可用容量。 */
function reportedBudget(value: number): number | null {
  return Number.isFinite(value) ? value : null
}

/** 仅使用调用方配置，不查询设备能力或生成默认容量。 */
export function resolveGltfResourceBudget(
  loading: GltfLoadingOptions,
  explicitTextureBudget?: number,
  loadedMeshGpuBytes = 0
): GltfResourceBudget {
  const snapshot = loading.texture.autoSize
  const ratio = snapshot.resourceUsageRatio
  if (!Number.isFinite(ratio) || ratio <= 0 || ratio > 1)
    throw new RangeError('资源比例必须在 (0, 1] 内')
  const cpu = available(snapshot.availableSystemMemoryBytes, ratio)
  const hostAvailable = available(snapshot.availableGpuMemoryBytes, 1)
  const capacity = available(snapshot.totalGpuMemoryBytes, 1)
  const hostUsed = available(snapshot.usedGpuMemoryBytes, 1)
  const trackedUsed = available(loadedMeshGpuBytes, 1)!
  const reserved = available(snapshot.reservedGpuMemoryBytes, 1) ?? 0
  const gpuSource = hostAvailable !== undefined || capacity !== undefined ? 'host' : 'unconfigured'
  const used = hostAvailable !== undefined ? 0 : (hostUsed ?? trackedUsed)
  const gpuAvailable =
    hostAvailable ?? (capacity === undefined ? Infinity : Math.max(0, capacity - used))
  const gpuBytes = Math.floor(Math.max(0, gpuAvailable - reserved) * ratio)
  const cpuLimit = configuredBudget(loading.budget.maxTransientBytes)
  const textureLimit = configuredBudget(explicitTextureBudget ?? loading.budget.maxTextureBytes)
  return {
    cpuBytes: reportedBudget(Math.min(cpu ?? Infinity, cpuLimit)),
    gpuBytes: reportedBudget(gpuBytes),
    gpuCapacityBytes: hostAvailable === undefined ? (capacity ?? null) : null,
    gpuUsedBytes: used,
    gpuUsedSource:
      hostAvailable !== undefined
        ? 'included-in-available'
        : hostUsed !== undefined
          ? 'host'
          : trackedUsed > 0
            ? 'tracked-mesh'
            : 'none',
    gpuAvailableBytes: reportedBudget(gpuAvailable),
    gpuReservedBytes: reserved,
    textureBytes: reportedBudget(Math.min(gpuBytes, textureLimit)),
    cpuSource:
      cpu !== undefined && cpu <= cpuLimit
        ? 'host'
        : Number.isFinite(cpuLimit)
          ? 'explicit'
          : 'unconfigured',
    gpuSource,
    textureSource: Number.isFinite(textureLimit) ? 'explicit' : gpuSource,
  }
}

function largestSum(values: number[], count: number): number {
  return values
    .sort((left, right) => right - left)
    .slice(0, count)
    .reduce((sum, value) => sum + value, 0)
}

/** 对已经收集的资源计算候选，整单常驻求和，临时开销按并发求峰值。 */
export function planGltfResources(
  assets: readonly GltfPreflightAsset[],
  loading: GltfLoadingOptions,
  explicitTextureBudget?: number,
  maxConcurrentLoads = 1,
  elapsedMs = 0,
  loadedMeshGpuBytes = 0
): GltfResourcePreflightReport {
  const budget = resolveGltfResourceBudget(loading, explicitTextureBudget, loadedMeshGpuBytes)
  const geometry = createEmptyGeometryEstimate()
  for (const asset of assets) {
    geometry.vertexCount += asset.geometry.vertexCount
    geometry.indexCount += asset.geometry.indexCount
    geometry.vertexAttributeBytes += asset.geometry.vertexAttributeBytes
    geometry.indexBytes += asset.geometry.indexBytes
    geometry.instanceBytes += asset.geometry.instanceBytes
  }
  const geometryBytes = getEstimatedGeometryBytes(geometry)
  const availableTextureBytes = Math.min(
    budget.textureBytes ?? Infinity,
    Math.max(0, (budget.gpuBytes ?? Infinity) - geometryBytes)
  )
  const diagnostics = assets.flatMap((asset) => asset.diagnostics)
  const unknownImageCount = assets.reduce((sum, asset) => sum + asset.images.length, 0)
  const hasKtx2 = assets.some((asset) => asset.images.some((image) => image.ktx2))
  const hasDraco = assets.some((asset) => asset.dracoUnknown)
  const evaluate = (
    maxSize: number | null,
    textureConcurrency: number,
    loadConcurrency: number
  ): GltfResourceCandidate => {
    const unknownOriginal = maxSize === null && unknownImageCount > 0
    const unknownCpu = unknownOriginal || hasKtx2 || hasDraco
    let bitmapBytes = 0
    let textureBytes = 0
    const decodes: number[] = []
    for (const asset of assets) {
      // 原始尺寸没有可推算的边长；对应总量在报告中置为 null。
      const bytes = maxSize === null ? 0 : maxSize * maxSize * 4
      bitmapBytes += asset.images.filter((image) => !image.ktx2).length * bytes
      for (const image of asset.images)
        decodes.push((image.ktx2 ? 0 : bytes * 2) + (image.encodedBytes ?? 0))
      // KTX2 的已有 mip 即使不参与采样也会上传，统一按 RGBA8 完整链保守计费。
      for (const texture of asset.textures)
        textureBytes += Math.ceil(bytes * (texture.ktx2 || texture.mipmaps ? 4 / 3 : 1))
    }
    const cpuResident = geometryBytes + bitmapBytes
    const cpuPeak =
      cpuResident +
      largestSum(
        decodes,
        textureConcurrency * (loading.mode === 'standard' ? 1 : loadConcurrency)
      ) +
      largestSum(
        assets.map((asset) => asset.transientBytes),
        loadConcurrency
      )
    const gpuBytes = geometryBytes + textureBytes
    return {
      maxSize,
      textureConcurrency,
      loadConcurrency,
      estimatedGeometryBytes: geometryBytes,
      knownTextureBytes: textureBytes,
      knownCpuPeakBytes: cpuPeak,
      knownGpuBytes: gpuBytes,
      estimatedTextureBytes: unknownOriginal ? null : textureBytes,
      estimatedCpuResidentBytes: unknownCpu ? null : cpuResident,
      estimatedCpuPeakBytes: unknownCpu ? null : cpuPeak,
      estimatedGpuBytes: unknownOriginal ? null : gpuBytes,
      availableTextureBytes: reportedBudget(availableTextureBytes),
      remainingGpuBytes:
        budget.gpuBytes === null || unknownOriginal
          ? null
          : Math.max(0, budget.gpuBytes - gpuBytes),
      fitsBudget:
        cpuPeak > (budget.cpuBytes ?? Infinity) ||
        gpuBytes > (budget.gpuBytes ?? Infinity) ||
        geometryBytes > configuredBudget(loading.budget.maxFinalGeometryBytes) ||
        textureBytes > (budget.textureBytes ?? Infinity)
          ? false
          : unknownOriginal || hasDraco || (unknownCpu && budget.cpuBytes !== null)
            ? null
            : true,
    }
  }
  const textureConcurrency = Math.max(
    1,
    Math.min(16, Math.floor(loading.texture.maxConcurrentDecodes))
  )
  const loadConcurrency = Math.max(1, Math.min(8, Math.floor(maxConcurrentLoads)))
  const original = evaluate(null, textureConcurrency, loadConcurrency)
  const sizes =
    loading.texture.maxSize === 'auto'
      ? [...new Set(loading.texture.autoSize.candidateSizes)]
          .filter((size) => Number.isInteger(size) && size > 0)
          .sort((left, right) => right - left)
      : [Number.isFinite(loading.texture.maxSize) ? loading.texture.maxSize : null]
  if (sizes.length === 0) throw new RangeError('至少需要一个有效纹理候选尺寸')
  const candidates: GltfResourceCandidate[] = []
  let selected: GltfResourceCandidate | undefined
  for (const size of sizes) {
    let candidate = evaluate(size, textureConcurrency, loadConcurrency)
    for (let loads = loadConcurrency; loads >= 1 && candidate.fitsBudget === false; loads -= 1) {
      for (let textures = textureConcurrency; textures >= 1; textures -= 1) {
        candidate = evaluate(size, textures, loads)
        if (candidate.fitsBudget !== false) break
      }
    }
    candidates.push(candidate)
    if (candidate.fitsBudget !== false && !selected) selected = candidate
  }
  return {
    geometry,
    textureCount: assets.reduce((sum, asset) => sum + asset.textures.length, 0),
    imageCount: assets.reduce((sum, asset) => sum + asset.images.length, 0),
    unknownImageCount,
    readBytes: assets.reduce((sum, asset) => sum + asset.readBytes, 0),
    elapsedMs,
    budget,
    original,
    candidates,
    selected,
    confidence: diagnostics.length > 0 ? 'partial' : 'estimated',
    diagnostics,
  }
}

/** 仅读取清单及其子 GLB 元数据；失败时取消并等待全部活动预检退出。 */
async function collectMeshResultPreflightAssets(
  request: MeshLoadRequest,
  loading: GltfLoadingOptions,
  dracoEnabled = false
): Promise<{ assets: GltfPreflightAsset[]; loadConcurrency: number; manifestReadBytes: number }> {
  const source = describeMeshSource(request)
  const baseUrl = resolveMeshBaseUrl(request)
  if (!baseUrl)
    throw new InvalidGltfManifestError(source, '分块清单需要 url 或 baseUrl 来解析 GLB 路径')
  const input =
    typeof request.data === 'string'
      ? new TextEncoder().encode(request.data)
      : (request.data ?? request.url)
  if (input === undefined) throw new InvalidGltfManifestError(source, '缺少分块清单来源')
  const reader = new GltfPreflightReader({ ...request, gltf: loading })
  const bytes = await reader.readAll(input)
  const manifest = parseMeshResultManifest(new TextDecoder().decode(bytes), baseUrl)
  const loadConcurrency = normalizeMaxConcurrentLoads(request.meshResult?.maxConcurrentLoads)
  const assets: GltfPreflightAsset[] = new Array(manifest.glbs.length)
  const controller = new AbortController()
  const abort = () => controller.abort(request.signal?.reason)
  if (request.signal?.aborted) abort()
  else request.signal?.addEventListener('abort', abort, { once: true })
  let nextIndex = 0
  let failure: { error: unknown } | undefined
  try {
    await Promise.all(
      Array.from({ length: Math.min(loadConcurrency, assets.length) }, async () => {
        while (!failure && nextIndex < assets.length) {
          const index = nextIndex++
          try {
            throwIfMeshLoadCancelled(controller.signal, source)
            assets[index] = await collectGltfPreflightAsset(
              {
                ...request,
                url: manifest.glbs[index].url,
                data: undefined,
                baseUrl: undefined,
                format: 'glb',
                signal: controller.signal,
                onPreflight: undefined,
                onProgress: undefined,
              },
              'glb',
              loading,
              undefined,
              undefined,
              dracoEnabled
            )
          } catch (error) {
            if (!failure) {
              failure = { error }
              controller.abort(error)
            }
          }
        }
      })
    )
    if (failure) throw failure.error
    throwIfMeshLoadCancelled(request.signal, source)
    return { assets, loadConcurrency, manifestReadBytes: reader.readBytes }
  } finally {
    request.signal?.removeEventListener('abort', abort)
  }
}

/** 独立预检 glTF/GLB 或 mesh_result.json 全部分块；只计算总需求，不启动正式加载。 */
export async function preflightGltfResources(
  request: MeshLoadRequest,
  options?: GltfResourcePreflightOptions
): Promise<GltfResourcePreflightReport> {
  const startedAt = performance.now()
  const loading = resolveGltfLoadingOptions(options, request.gltf)
  const format = request.format?.toLowerCase() ?? getMeshSourceExtension(request.url) ?? 'glb'
  const { assets, loadConcurrency, manifestReadBytes } =
    format === 'json' || format === 'mesh-result-json'
      ? await collectMeshResultPreflightAssets(request, loading, options?.draco === true)
      : {
          assets: [
            await collectGltfPreflightAsset(
              request,
              format === 'gltf' ? 'gltf' : 'glb',
              loading,
              undefined,
              undefined,
              options?.draco === true
            ),
          ],
          loadConcurrency: 1,
          manifestReadBytes: 0,
        }
  const report = planGltfResources(
    assets,
    loading,
    request.gltf?.budget?.maxTextureBytes ?? options?.budget?.maxTextureBytes,
    loadConcurrency,
    performance.now() - startedAt
  )
  return { ...report, readBytes: report.readBytes + manifestReadBytes }
}
